import { t } from "../../lib/i18n";
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import api, { getBlogArticlesMetadata } from '../../api'; // Import centralized api instance
import PublicNavbar from '../../components/PublicNavbar';
import Navbar from '../../components/Navbar';
import { auth } from '../../firebaseClient';
import LeaseInsightWidget from '../../components/blog/LeaseInsightWidget';
import BottomCTA from '../../components/blog/BottomCTA';
import ArticleCard from '../../components/blog/ArticleCard';
import { getAuthorForSlug } from '../../data/authors';

interface Article {
  title: string;
  meta_title?: string;
  meta_description?: string;
  slug: string;
  publish_date?: string;
  published_at?: string; 
  content?: string;
  html_content?: string;
  featured_image?: string;
  thumbnail_url?: string;
}

interface ArticleMetadata {
  title: string;
  slug: string;
  featured_image?: string;
  thumbnail_url?: string;
}

const ArticleDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [article, setArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [relatedArticles, setRelatedArticles] = useState<ArticleMetadata[]>([]);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean | null>(null);

  useEffect(() => {
    const checkLoginStatus = () => {
      setIsLoggedIn(!!auth.currentUser);
    };
    checkLoginStatus();

    const fetchData = async () => {
      if (!slug) return;
      
      try {
        setLoading(true);
        setError(null);

        // Fetch current article using the centralized API instance
        let articleData: Article | null = null;
        try {
          const articleResponse = await api.get<Article>(`/blog/articles/${slug}`);
          articleData = articleResponse.data;
        } catch (apiErr: any) {
          if (apiErr.response?.status === 404) {
            console.log("Article not found in cache, requesting real-time sync from GCS...");
            // Use no_cache=true to force backend to bypass cache and fetch directly from GCS
            const syncResponse = await api.get<Article>(`/blog/articles/${slug}?no_cache=true`);
            articleData = syncResponse.data;
          } else {
            throw apiErr;
          }
        }
        
        if (articleData) {
          setArticle(articleData);
        }

        // Fetch all articles metadata for related articles
        const allArticles = await getBlogArticlesMetadata();
        const filteredRelated = allArticles.filter(
          (art: ArticleMetadata) => art.slug !== slug
        );

        const shuffled = filteredRelated.sort(() => 0.5 - Math.random());
        setRelatedArticles(shuffled.slice(0, 4));

      } catch (err) {
        console.error(`Error fetching data for article ${slug}:`, err);
        setError('Failed to load article or related content.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [slug]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading || isLoggedIn === null) return <div className="text-center p-8">Loading...</div>; // Handle loading state for auth check
  if (error) return <div className="text-center p-8 text-red-500">{t("articleDetailPage.k1")} {error}</div>;
  if (!article) return <div className="text-center p-8">{t("articleDetailPage.k2")}</div>;

  const creImages = [
    "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=1200", // Glass skyscraper
    "https://images.unsplash.com/photo-1554469384-e58fac16e23a?q=80&w=1200", // Corporate building
    "https://images.unsplash.com/photo-1497366216548-37526070297c?q=80&w=1200", // Office conference room
    "https://images.unsplash.com/photo-1497366754035-f200968a6e72?q=80&w=1200", // Sleek co-working space
    "https://images.unsplash.com/photo-1497215728101-856f4ea42174?q=80&w=1200", // Modern office desk
    "https://images.unsplash.com/photo-1577495508048-b635879837f1?q=80&w=1200", // Modern exterior
    "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?q=80&w=1200", // Premium corporate property
    "https://images.unsplash.com/photo-1556800045-813d10008efc?q=80&w=1200"  // Elegant commercial lobby
  ];
  let hash = 0;
  if (slug) {
    for (let i = 0; i < slug.length; i++) {
      hash = (hash * 31 + slug.charCodeAt(i)) | 0;
    }
  }
  const fallbackImage = creImages[Math.abs(hash) % creImages.length];
  
  let detailImageUrl = article.thumbnail_url || article.featured_image || fallbackImage;
  if (detailImageUrl && detailImageUrl.startsWith("https://storage.googleapis.com/") && detailImageUrl.includes("X-Goog-Algorithm=")) {
    detailImageUrl = detailImageUrl.split("?")[0];
  }
  
  const rawContent = article.html_content || article.content || '';
  
  // Logic to split content for widgets (only for public visitors)
  let firstPart = rawContent;
  let secondPart = '';
  const showWidgets = !isLoggedIn;

  if (showWidgets) {
    const paragraphs = rawContent.split('</p>');
    if (paragraphs.length >= 2) {
      const midPoint = Math.max(1, Math.floor(paragraphs.length * 0.35));
      firstPart = paragraphs.slice(0, midPoint).join('</p>') + '</p>';
      secondPart = paragraphs.slice(midPoint).join('</p>');
    }
  }

  const author = getAuthorForSlug(slug || article.slug || '');

  return (
    <>
      <Helmet>
        <title>{article.meta_title || article.title}</title>
        <meta name="description" content={article.meta_description || `Read our expert guide on ${article.title}`} />
        <link rel="canonical" href={`https://credominion.com/blog/${article.slug}`} />
        <meta property="og:title" content={article.meta_title || article.title} />
        <meta property="og:description" content={article.meta_description} />
        <meta property="og:type" content="article" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": article.title,
            "datePublished": article.publish_date || article.published_at,
            "author": {
              "@type": "Person",
              "name": author.name,
              "jobTitle": author.role,
              "worksFor": {
                "@type": "Organization",
                "name": "CRE Dominion"
              }
            },
            "publisher": {
              "@type": "Organization",
              "name": "CRE Dominion",
              "logo": {
                "@type": "ImageObject",
                "url": "https://credominion.com/logo.png"
              }
            },
            "description": article.meta_description
          })}
        </script>
      </Helmet>
      {isLoggedIn ? <Navbar handleSignOut={async () => { await auth.signOut(); setIsLoggedIn(false); }} /> : <PublicNavbar />}
      <div className="container mx-auto p-4 max-w-4xl relative">
        <Link to="/blog" className="inline-flex items-center text-blue-600 hover:text-blue-800 mb-6 font-medium">
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
          </svg>
          {t("articleDetailPage.k4")} </Link>

        {/* Author Header Bar */}
        <div className="flex items-center gap-4 mb-6 pb-4 border-b border-gray-100 text-left">
          <img 
            src={author.avatar} 
            alt={author.name} 
            className="w-12 h-12 rounded-full object-cover shadow-sm border border-gray-200" 
          />
          <div>
            <div className="font-bold text-gray-900 text-sm">{author.name}</div>
            <div className="text-xs text-gray-500">
              {author.role} {t("articleDetailPage.k5")} {new Date(article.published_at || article.publish_date || new Date().toISOString()).toLocaleDateString()}
            </div>
          </div>
        </div>
        
        <div className="mb-8 rounded-xl overflow-hidden shadow-lg">
          <img 
            src={detailImageUrl} 
            alt={article.title} 
            className="w-full h-auto"
          />
        </div>

        <div className="prose lg:prose-xl max-w-none text-left">
          {showWidgets && secondPart ? (
            <>
              <div dangerouslySetInnerHTML={{ __html: firstPart }} />
              <LeaseInsightWidget slug={article.slug} />
              <div dangerouslySetInnerHTML={{ __html: secondPart }} />
              <BottomCTA />
            </>
          ) : (
            <div dangerouslySetInnerHTML={{ __html: rawContent }} />
          )}
        </div>

        {/* Author Bio Card for E-E-A-T */}
        <div className="my-10 p-6 bg-blue-50/60 border border-blue-100 rounded-2xl flex flex-col sm:flex-row items-center sm:items-start gap-5 text-left">
          <img 
            src={author.avatar} 
            alt={author.name} 
            className="w-20 h-20 rounded-full object-cover shadow-md border-2 border-white shrink-0" 
          />
          <div>
            <div className="text-xs uppercase font-bold text-blue-600 tracking-wider mb-1">{t("articleDetailPage.k6")}</div>
            <h4 className="text-lg font-bold text-gray-900">{author.name}</h4>
            <p className="text-xs text-blue-800 font-medium mb-2">{author.role} • {author.credentials}</p>
            <p className="text-sm text-gray-700 leading-relaxed">{author.bio}</p>
          </div>
        </div>

        <div className="flex justify-center mt-12 mb-8">
          <button 
            onClick={scrollToTop}
            className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors duration-200 shadow-sm"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 10l7-7m0 0l7 7m-7-7v18"></path>
            </svg>
            {t("articleDetailPage.k7")} </button>
        </div>

        {relatedArticles.length > 0 && (
          <div className="mt-16 pt-12 border-t border-gray-200 text-left">
            <h2 className="text-3xl font-bold mb-8 text-gray-800">{t("articleDetailPage.k8")}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedArticles.map((ra) => (
                <div key={ra.slug} className="flex flex-col h-full">
                  <ArticleCard article={ra} />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ArticleDetailPage;
